import { Box, Grid, Typography } from "@material-ui/core";
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import chroma from "chroma-js";
import React, { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartTooltip, XAxis, YAxis } from 'recharts';
import { useAppState } from "../../context/zustand";
import { useAxios } from "../../utils/request";
import { getCollectedWeekRange } from "../../utils/weekInterval";
import { CustomTooltip, getWeekInterval, sumObjectItems, sumObjectsByKey } from "../../utils/utils";
import CheckboxList from "../CheckboxList";
import KoefPopover from "../KoefPopover";
import { useEpisodeSelectionConsumer } from "../../hooks/useEpisodeSelectionConsumer";
import { EPISODE_MODE_ALL, EPISODE_MODE_NONE, getEpisodeSelectionRequest } from "../../utils/episodeSelection";



const useStyles = makeStyles(() => ({
    root: {
        paddingInline: 30
    },
}));



export default function PodcastModul(props) {
    const classes = useStyles();
    const podcast = useAppState(s => s.podcast?.name);
    const episodeMode = useAppState(s => s.episodeMode);
    const selectedEpisodeIds = useAppState(s => s.selectedEpisodeIds);
    const intervalPreset = useAppState(s => s.intervalPreset);
    const intervalStartWeek = useAppState(s => s.intervalStartWeek);
    const intervalEndWeek = useAppState(s => s.intervalEndWeek);
    const [data, setData] = useState([]);
    const [rawData, setRawData] = useState(false);
    const [platformKoef, setPlatformKoef] = useState(props.coef);
    const selectionRequest = getEpisodeSelectionRequest(episodeMode, selectedEpisodeIds);
    const selectedEpisodeParam = selectionRequest.episodes;
    const allEpisodes = episodeMode === EPISODE_MODE_ALL;
    const { revision, acknowledgeAfterPaint } = useEpisodeSelectionConsumer('podcast');

    const getAllEpisodesRequest = useAxios(
        ["allepisodes", podcast, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek],
        {
            method: 'get',
            url: `/api/allepisodes/`,
            params: {
                episodes: selectedEpisodeParam,
                date_from: intervalStartWeek,
                date_to: intervalEndWeek,
                podcast: podcast
            }
        },
        {enabled: Boolean(podcast) && selectionRequest.enabled },
        () => {},
        () => {}
    )

    useEffect(() => {
        if (episodeMode === EPISODE_MODE_NONE) {
            setRawData(false);
            setData([]);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (getAllEpisodesRequest.isFetching) {
            return;
        }

        if (getAllEpisodesRequest.isError) {
            setRawData(false);
            setData([]);
            acknowledgeAfterPaint(revision);
            return;
        }

        if (getAllEpisodesRequest.data) {
            const responseData = getAllEpisodesRequest.data.data;
            setRawData(responseData);
            setData(processData(responseData));
            acknowledgeAfterPaint(revision);
        }
        // eslint-disable-next-line
    }, [
        acknowledgeAfterPaint,
        episodeMode,
        getAllEpisodesRequest.data,
        getAllEpisodesRequest.isError,
        getAllEpisodesRequest.isFetching,
        revision,
    ]);

    useEffect(() => {
        setPlatformKoef(props.coef);
    }, [props.coef])

    function getEpisodeColor(id, len) {
        return chroma.scale(['#fafa6e', '#2A4858']).mode('lch').colors(len)[id]
    }

    function processData(resData) {

        const collectedRange = getCollectedWeekRange(intervalStartWeek, intervalEndWeek, resData.minDateValue, resData.maxDateValue);

        if (!collectedRange) {
            return []
        }

        let data = JSON.parse(JSON.stringify(resData.results))
        let dateInterval = getWeekInterval(collectedRange.startWeek, collectedRange.endWeek);
        let newArray = []

        dateInterval.reduce((acc, curr, index) => {

            if (curr in data) {

                let episodes = {}
                Object.keys(data[curr]).forEach(episode => {
                    episodes[episode] = sumObjectItems(data[curr][episode], platformKoef)
                })

                let episode = sumObjectsByKey(acc, episodes);

                if (allEpisodes) {
                    newArray[index] = { 'date': curr, 'all_episodes': sumObjectItems(episode, {}) };
                }
                else {
                    newArray[index] = { 'date': curr, ...episode };

                }
                return episode
            }
            else {
                if (allEpisodes) {
                    newArray[index] = { 'date': curr, 'all_episodes': sumObjectItems(acc, {}) };
                }
                else {
                    newArray[index] = { 'date': curr, ...acc };

                }
                return acc
            }

        }, {})
        return newArray
    }

    useEffect(() => {

        if (episodeMode !== EPISODE_MODE_NONE && rawData) {
            let data = processData(rawData);
            setData(data);
        }
        // eslint-disable-next-line
    }, [platformKoef, rawData, episodeMode, selectedEpisodeParam, intervalPreset, intervalStartWeek, intervalEndWeek])

    const isGraphLoading = getAllEpisodesRequest.isFetching;

    return (
        <>
            <Grid container spacing={3} className={classes.root}>
                <Grid item lg={12}>
                    <Box paddingY={5} display="flex" justifyContent="space-between" alignItems="center" flexDirection="row" >
                        <Typography align="left" variant='h4'>Podcast downloads</Typography>
                        <Box display="flex" flexDirection="row" >
                            <Box paddingRight={2}>
                                <KoefPopover
                                    values={platformKoef}
                                    handleChange={
                                        (value) => {
                                            setPlatformKoef(value)
                                        }
                                    } />
                            </Box>
                            <CheckboxList
                                values={platformKoef}
                                defaultValues={props.coef}
                                handleChange={
                                    (value) => {
                                        setPlatformKoef(value);
                                    }
                                }
                            />
                        </Box>

                    </Box>

                </Grid>
                <Grid item xs={12} md={12} lg={12}>
                    {episodeMode === EPISODE_MODE_NONE ? (
                        <Box height={500} display="flex" alignItems="center" justifyContent="center">
                            <Typography color="textSecondary">Select at least one episode.</Typography>
                        </Box>
                    ) : isGraphLoading ? (
                        <Box height={500} display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <ResponsiveContainer width="100%" height={500}>
                            <AreaChart
                                data={data}
                                margin={{
                                    top: 0,
                                    right: 0,
                                    left: 0,
                                    bottom: 0,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <RechartTooltip content={<CustomTooltip />} />
                                {data[data.length - 1] && Object.keys(data[data.length - 1]).reduce((acc, item, index) => {
                                    let len = Object.keys(data[data.length - 1]).length
                                    if (item !== 'date') {
                                        acc.push(<Area type="monotone" key={`${item} ${index} `} activeDot={{ strokeWidth: 2 }} dot={{ strokeWidth: 5 }} dataKey={item} stackId="1" stroke={getEpisodeColor(index, len)} fill={getEpisodeColor(index, len)} />)
                                    }
                                    return acc

                                }, [])}
                            </AreaChart>
                        </ResponsiveContainer>
                    )}

                </Grid>
            </Grid >
        </>
    )
}
